/* eslint-disable max-lines */
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import FormWrapper from "../../components/ui/FormWrapper";
import Input from "../../components/ui/input/Input";
import Textarea from "../../components/ui/input/Textarea";
import { IBlockedUser } from "../../interfaces/endpoints/IBlockedUser";
import {
  createBlockedUser,
  updateBlockedUser
} from "../../services/BlockedUser.service";
import BlockedUserSchema from "../../utils/validations/BlockedUser.schema";

const AddBlockedUser: React.FC<{ editMode?: boolean }> = ({ editMode }) => {
  const { id } = useParams();
  const { state }: any = useLocation();
  const navigate = useNavigate();

  const methods = useForm<IBlockedUser>({
    resolver: yupResolver(BlockedUserSchema),
    defaultValues: {
      email: "",
      blockEsimReservationNlb: false,
      blockEsimReservationStripe: false,
      blockJourneyReservationNlb: false,
      blockJourneyReservationStripe: false,
      blockAll: false,
      blockReason: ""
    }
  });

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors }
  } = methods;

  const blockAll = watch("blockAll");

  useEffect(() => {
    if (editMode && state?.userData) {
      // Use data passed from navigation state
      const userData = state.userData as IBlockedUser;
      Object.entries(userData).forEach(([key, value]) => {
        const blockedUserKey = key as keyof IBlockedUser;
        setValue(blockedUserKey, value);
      });
    }
  }, [editMode, state, setValue]);

  // When blockAll is true, set all other blocks to true
  useEffect(() => {
    if (blockAll) {
      setValue("blockEsimReservationNlb", true);
      setValue("blockEsimReservationStripe", true);
      setValue("blockJourneyReservationNlb", true);
      setValue("blockJourneyReservationStripe", true);
    }
  }, [blockAll, setValue]);

  const onSubmit: SubmitHandler<IBlockedUser> = async (data) => {
    if (editMode && id) {
      const updateData = {
        blockEsimReservationNlb: data.blockEsimReservationNlb,
        blockEsimReservationStripe: data.blockEsimReservationStripe,
        blockJourneyReservationNlb: data.blockJourneyReservationNlb,
        blockJourneyReservationStripe: data.blockJourneyReservationStripe,
        blockAll: data.blockAll,
        blockReason: data.blockReason
      };

      await updateBlockedUser(Number(id), updateData)
        .then(() => {
          toast.success("Përdoruesi është ndryshuar me sukses.", {
            position: "top-right"
          });
          setTimeout(() => {
            navigate(
              `/blocked-users?pageindex=${state?.page ? state?.page : 1}`,
              {
                state: ["Blocked Users"]
              }
            );
          }, 500);
        })
        .catch(() => toast.error("Ndodhi një gabim gjatë ndryshimit."));
    } else {
      const createData = {
        email: data.email,
        blockEsimReservationNlb: data.blockEsimReservationNlb,
        blockEsimReservationStripe: data.blockEsimReservationStripe,
        blockJourneyReservationNlb: data.blockJourneyReservationNlb,
        blockJourneyReservationStripe: data.blockJourneyReservationStripe,
        blockAll: data.blockAll,
        blockReason: data.blockReason
      };

      await createBlockedUser(createData)
        .then(() => {
          toast.success("Përdoruesi është shtuar në listë të zezë me sukses.", {
            position: "top-right"
          });
          setTimeout(() => {
            navigate("/blocked-users", { state: ["Blocked Users"] });
          }, 500);
        })
        .catch(() =>
          toast.error("Ndodhi një gabim gjatë krijimit.", {
            position: "top-right"
          })
        );
    }
  };

  return (
    <FormProvider {...methods}>
      <Link
        className="flex items-center mb-4 text-sm font-semibold text-primary"
        to={"/blocked-users"}
        state={["Blocked Users"]}
      >
        <ChevronLeftIcon className="w-4 h-4 mr-2" />
        <span>Kthehu tek lista</span>
      </Link>
      <FormWrapper
        showPrompt={
          !methods.formState.isSubmitSuccessful &&
          !!Object.keys(methods.formState.dirtyFields).length
        }
        onSubmit={handleSubmit(onSubmit)}
        title={
          editMode
            ? "Modifiko përdoruesin në listë të zezë"
            : "Shto përdorues në listë të zezë"
        }
      >
        <Input
          {...register("email")}
          label="Email"
          className="mb-6"
          placeholder="Shkruaj email këtu..."
          error={errors.email?.message}
          disabled={editMode}
        />

        <div className="mb-6">
          <label className="flex items-center mb-2">
            <input
              type="checkbox"
              {...register("blockAll")}
              className="mr-2 w-4 h-4"
            />
            <span className="text-sm font-medium">
              Block All (Bllokon të gjitha rezervimet)
            </span>
          </label>
        </div>

        <div className="mb-6 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">
            Opsionet specifike të bllokimit:
          </h3>

          <label className="flex items-center">
            <input
              type="checkbox"
              {...register("blockEsimReservationNlb")}
              className="mr-2 w-4 h-4"
              disabled={blockAll}
            />
            <span className="text-sm">Block eSIM Reservation (NLB)</span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              {...register("blockEsimReservationStripe")}
              className="mr-2 w-4 h-4"
              disabled={blockAll}
            />
            <span className="text-sm">Block eSIM Reservation (Stripe)</span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              {...register("blockJourneyReservationNlb")}
              className="mr-2 w-4 h-4"
              disabled={blockAll}
            />
            <span className="text-sm">Block Journey Reservation (NLB)</span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              {...register("blockJourneyReservationStripe")}
              className="mr-2 w-4 h-4"
              disabled={blockAll}
            />
            <span className="text-sm">Block Journey Reservation (Stripe)</span>
          </label>
        </div>

        <Textarea
          className="mb-6 text-sm"
          placeholder="Shkruaj arsyen e bllokimit (opsionale)..."
          label="Arsyeja e bllokimit (Opsionale)"
          {...register("blockReason")}
          error={errors.blockReason?.message}
        />
      </FormWrapper>
    </FormProvider>
  );
};

export default AddBlockedUser;
